import { Component } from '@verndale/core';
import { gsap } from 'gsap';
import { v4 as uuidv4 } from 'uuid';
import { isFocusable, keyCode } from '../helpers';

class Module extends Component {
  setupDefaults() {
    this.handleEscape = this.handleEscape.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.ignoreUtilFocusChanges = false;

    this.dom = {
      primaryLinkContainers: [
        ...this.el.querySelectorAll('.ir-sub-nav')
      ],
      primaryLinks: this.el.querySelectorAll('.ir-sub-nav__primary-link'),
      submenus: [...this.el.querySelectorAll('.ir-sub-nav__menu-submenu')],
      dropdownTriggerOuter: this.el.querySelectorAll('.dropdown__trigger-outer'),
      dropdownTriggerInner: this.el.querySelectorAll('.dropdown__trigger-inner'),
      dropdownListOuter: [...this.el.querySelectorAll('.dropdown__list-outer')],
      dropdownListInner: [...this.el.querySelectorAll('.dropdown__list-inner')],
      menu: this.el.querySelector('.ir-sub-nav__sticky'),
      primaryNav: this.el.querySelector('.ir-sub-nav__primary-nav'),
      outerDropdown: this.el.querySelector('.dropdown__outer'),
      innerDropdown: [...this.el.querySelectorAll('dropdown__inner')],
      menuSmDevice: this.el.querySelector('.ir-sub-nav__menu-sm-device')
    };
  }

  addListeners() {
    window.addEventListener('resize', this.resetState.bind(this));


    this.dom.primaryLinks.forEach((link, index) => {
      const guid = uuidv4();
      if (this.dom.submenus[index]) {
        this.dom.submenus[index].id = guid;
      }
      link.setAttribute('aria-controls', guid);
      link.addEventListener('click', this.handlePrimaryLinkSelect.bind(this));

    });

    this.dom.dropdownTriggerOuter.forEach((link, index) => {
      const guid = uuidv4();
      if (this.dom.dropdownListOuter[index]) {
        this.dom.dropdownListOuter[index].id = guid;
      }
      link.setAttribute('aria-controls', guid);
      link.addEventListener('click', this.handleOuterDropdownClick.bind(this));
    });

    this.dom.dropdownTriggerInner.forEach((link, index) => {
      const guid = uuidv4();
      if (this.dom.dropdownListInner[index]) {
        this.dom.dropdownListInner[index].id = guid;
      }
      link.setAttribute('aria-controls', guid);
      link.addEventListener('click', this.handleInnerDropdownClick.bind(this));
    });

    document.addEventListener('click', this.handleClickOutside.bind(this));
    document.addEventListener('scroll', this.handleScroll.bind(this));

  }

  addEventListeners() {
    document.addEventListener('keyup', this.handleEscape);
    document.addEventListener('focus', this.handleFocus, true);


  }

  handleScroll() {
    const hero = document.querySelector('[data-hero="true"]');
    const { offsetHeight, offsetTop } = hero;
    const subnavHeight = this.el.offsetHeight;
    const headerHeight = document.querySelector('.header.v2').offsetHeight;
    const scroll = window.scrollY;
    const pageContentScroll = scroll - offsetTop;

    if (pageContentScroll >= offsetHeight - (2 * subnavHeight) - headerHeight) {
      this.el.classList.add('scrolled');
    } else {
      this.el.classList.remove('scrolled');
    }
  }

  removeEventListeners() {
    document.removeEventListener('keyup', this.handleEscape);
    document.removeEventListener('focus', this.handleFocus, true);
    document.addEventListener('scroll', this.handleScroll);
  }

  handlePrimaryLinkSelect(e) {
    const id = e.currentTarget.getAttribute('aria-controls');
    if (this.activeSubmenu && this.activeSubmenu.id === id) {
      e.currentTarget.classList.remove('ir-sub-nav__primary-link-selected');
      this.hideSubmenus();
    } else {
      this.displaySubmenu(e.currentTarget);
    }
  }

  handleOuterDropdownClick(e) {
    const id = e.currentTarget.getAttribute('aria-controls');
    if (this.activeOuterDropdown && this.activeOuterDropdown.id === id) {
      this.hideOuterDropdown();
      this.hideInnerDropdown();
    } else {
      this.displayOuterDropdown(e.currentTarget);
    }
  }

  handleInnerDropdownClick(e) {
    const id = e.currentTarget.getAttribute('aria-controls');
    if (this.activeInnerDropdown && this.activeInnerDropdown.id === id) {
      e.currentTarget.classList.remove('ir-sub-nav__inner-dropdown-selected');
      this.hideInnerDropdown();
    } else {
      this.displayInnerDropdown(e.currentTarget);
    }
  }

  displaySubmenu(option) {
    this.hideSubmenus();
    this.activeSubmenu = this.dom.submenus.find(
      submenu => submenu.id === option.getAttribute('aria-controls')
    );
    option.classList.add('ir-sub-nav__primary-link-selected');
    const buttonRect = option.getBoundingClientRect();
    const x = buttonRect.left;
    this.activeSubmenu.style.left = `${x-24}px`;
    gsap.to(this.activeSubmenu, {
      height: 'auto',
      autoAlpha: 1,
      duration: 0.3
    });
  }

  displayOuterDropdown(option) {
    this.hideOuterDropdown();

    this.activeOuterDropdown = this.dom.dropdownListOuter.find(
      submenu => submenu.id === option.getAttribute('aria-controls')
    );

    option.parentNode.classList.add('ir-sub-nav__outer-menu-active');

    gsap.to(this.activeOuterDropdown, {
      height: 'auto',
      autoAlpha: 1,
      duration: 0.3
    });
  }

  displayInnerDropdown(option) {
    this.hideInnerDropdown();

    this.activeInnerDropdown = this.dom.dropdownListInner.find(
      submenu => submenu.id === option.getAttribute('aria-controls')
    );

    option.parentNode.classList.add('ir-sub-nav__inner-menu-active');

    gsap.to(this.activeInnerDropdown, {
      height: 'auto',
      autoAlpha: 1,
      duration: 0.3
    });
  }

  hideSubmenus() {
    if (this.activeSubmenu) {
      this.dom.primaryLinkContainers
        .find(link =>
          link.classList.contains('ir-sub-nav__menu-section--active')
        )
        ?.classList.remove('ir-sub-nav__menu-section--active');

      const links = [...this.dom.primaryLinks];

        links
          .find(link =>
            link.classList.contains('ir-sub-nav__primary-link-selected')
          )
        ?.classList.remove('ir-sub-nav__primary-link-selected');

        gsap.to(this.activeSubmenu, {
          height: 0,
          autoAlpha: 0,
          duration: 0.3
        });

        this.activeSubmenu = null;
    }
  }

  hideOuterDropdown() {
    if (this.activeOuterDropdown) {
      [...this.dom.dropdownTriggerOuter]
        .find(link =>
          link.parentNode.classList.contains('ir-sub-nav__outer-menu-active')
        )
        ?.parentNode.classList.remove('ir-sub-nav__outer-menu-active');

      gsap.to(this.activeOuterDropdown, {
        height: 0,
        autoAlpha: 0,
        duration: 0.3
      });
      this.activeOuterDropdown = null;
    }
  }

  hideInnerDropdown() {
    if (this.activeInnerDropdown) {
      [...this.dom.dropdownTriggerInner]
        .find(link =>
          link.parentNode.classList.contains('ir-sub-nav__inner-menu-active')
        )
        ?.parentNode.classList.remove('ir-sub-nav__inner-menu-active');

      gsap.to(this.activeInnerDropdown, {
        height: 0,
        autoAlpha: 0,
        duration: 0.3
      });
      this.activeInnerDropdown = null;
    }
  }

  handleSubmenuNavigate(e) {
    if (this.ignoreUtilFocusChanges) return;
    const { activeElement } = document;
    const submenuLinks = [...this.activeSubmenu?.querySelectorAll('a')];
    const activeSubmenu = submenuLinks.indexOf(activeElement);

    switch (e.keyCode) {
      case keyCode.TAB:
        if (this.activeSubmenu && this.activeSubmenu.contains(activeElement)) {
          e.preventDefault();

          this.attemptFocus(
            this.dom.primaryLinks.find(
              link =>
                link.getAttribute('aria-controls') === this.activeSubmenu.id
            )
          );
        }
        break;
      case keyCode.RIGHT:
      case keyCode.DOWN:
        if (this.activeSubmenu && this.activeSubmenu.contains(activeElement)) {
          e.preventDefault();
          if (activeSubmenu === submenuLinks.length - 1) {
            this.focusFirstDescendant(this.activeSubmenu);
          } else {
            this.attemptFocus(submenuLinks[activeSubmenu + 1]);
          }
        }
        break;
      case keyCode.LEFT:
      case keyCode.UP:
        if (this.activeSubmenu && this.activeSubmenu.contains(activeElement)) {
          e.preventDefault();
          if (activeSubmenu === 0) {
            this.focusLastDescendant(this.activeSubmenu);
          } else {
            this.attemptFocus(submenuLinks[activeSubmenu - 1]);
          }
        }
        break;
    }
  }

  handleClickOutside(e) {
    this.breakpoint = matchMedia('(min-width: 1200px)');
    if (this.breakpoint.matches) {
      if (!this.dom.menu.contains(e.target) && (!this.dom.primaryNav.contains(e.target))){
        this.hideSubmenus();
      }
    } else {
      if (!this.dom.menuSmDevice.contains(e.target)) {
        this.resetState();
      }
    }
  }

  resetState() {
    this.hideSubmenus();
    this.hideOuterDropdown();
    this.hideInnerDropdown();
  }

  focusFirstDescendant(element) {
    for (let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusFirstDescendant(child)) {
        return true;
      }
    }
    return false;
  }

  focusLastDescendant(element) {
    for (let i = element.childNodes.length - 1; i >= 0; i--) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusLastDescendant(child)) {
        return true;
      }
    }
    return false;
  }

  handleEscape(e) {
    const key = e.which || e.keyCode;

    if (key === keyCode.ESC) {
      e.stopPropagation();
    }
  }

  handleFocus(e) {
    if (
      this.ignoreUtilFocusChanges ||
      e.target.classList.contains('ir-sub-nav__menu__primary-link')
    )
      return;
  }

  attemptFocus(element) {
    if (!isFocusable(element)) {
      return false;
    }

    this.ignoreUtilFocusChanges = true;
    try {
      element.focus();
    } catch (e) {
      return;
    }
    this.ignoreUtilFocusChanges = false;
    return document.activeElement === element;
  }
}

export default Module;
